// src/components/agent/parts/FileCard.js
//
// FileDownloadCard, extracted from MessageDisplay.js (frontend spec §8.6),
// restyled for the page surface and with an **Open** action for every
// FilePane-supported type (was canvas-only). Download keeps the
// re-sign-from-gcsPath fallback for expired URLs.
import React, { useState } from 'react';
import { Box, Paper, Typography, Button, IconButton, Tooltip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TableChartIcon from '@mui/icons-material/TableChart';
import DescriptionIcon from '@mui/icons-material/Description';
import GridOnIcon from '@mui/icons-material/GridOn';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { getSignedUrl } from '../../../services/api_utils';
import { downloadBlob } from '../../../utils/canvasWorkbook';

export const getAttachmentIcon = (filename = '', kind = '') => {
  const ext = filename.split('.').pop()?.toLowerCase();
  if (kind === 'image' || ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return <ImageIcon />;
  if (kind === 'pdf' || ext === 'pdf') return <PictureAsPdfIcon />;
  if (['xlsx', 'xls'].includes(ext)) return <TableChartIcon />;
  if (ext === 'docx') return <DescriptionIcon />;
  if (ext === 'csv') return <GridOnIcon />;
  return <InsertDriveFileIcon />;
};

function FileCard({ filename, description, downloadUrl, gcsPath, sandboxPath, toolUseId, onOpenFile }) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  const handleDownload = async () => {
    try {
      setDownloading(true);
      setError(null);
      const resolvedUrl = downloadUrl || (gcsPath ? await getSignedUrl(gcsPath) : null);
      if (!resolvedUrl) {
        throw new Error('No download URL available.');
      }

      let response = await fetch(resolvedUrl);
      // Stale signed URL (historical cards) — re-sign and retry once.
      if ((response.status === 401 || response.status === 403) && gcsPath) {
        const freshUrl = await getSignedUrl(gcsPath);
        response = await fetch(freshUrl);
      }
      if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}`);
      }

      const blob = await response.blob();
      downloadBlob(blob, filename);
    } catch (err) {
      console.error('Failed to download assistant file:', err);
      setError(err.message || 'Download failed.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        backgroundColor: 'background.alt',
        borderColor: 'divider',
        borderRadius: 1.5,
        maxWidth: 520,
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 1.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'background.paper',
          color: 'text.secondary',
          flexShrink: 0,
        }}
      >
        {getAttachmentIcon(filename)}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ wordBreak: 'break-word' }}>{filename}</Typography>
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
            {description}
          </Typography>
        )}
        {error && (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        {onOpenFile && (
          <Tooltip title="Open">
            <IconButton
              size="small"
              onClick={() => onOpenFile({ filename, downloadUrl, gcsPath, sandboxPath, toolUseId })}
              sx={{
                color: 'text.primary',
                backgroundColor: 'background.paper',
                border: 1,
                borderColor: 'divider',
              }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Button
          variant="outlined"
          size="small"
          onClick={handleDownload}
          disabled={downloading}
          startIcon={<DownloadIcon />}
          sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
        >
          {downloading ? 'Preparing...' : 'Download'}
        </Button>
      </Box>
    </Paper>
  );
}

export default FileCard;
